/**
 * WebSocket Constants
 *
 * Configuration constants for AWS AppSync Events WebSocket integration.
 */

import { WebAppEnvironment } from '@/types/api';
import type { ReconnectionConfig } from '@/types/websocket';

// ------------- AppSync Protocol -------------

/**
 * AWS AppSync Events WebSocket subprotocol
 */
export const APPSYNC_SUBPROTOCOL = 'aws-appsync-event-ws';

// ------------- Environment URLs -------------

/**
 * WebSocket endpoint URLs for each environment
 */
export const WEBSOCKET_URLS: Record<WebAppEnvironment, string> = {
  [WebAppEnvironment.FRODO]: 'wss://events.user-management-service.frodo.howfm.io/event/realtime',
  [WebAppEnvironment.SAMWISE]: 'wss://events.user-management-service.samwise.howfm.io/event/realtime',
  [WebAppEnvironment.MERRY]: 'wss://events.user-management-service.merry.howfm.io/event/realtime',
  [WebAppEnvironment.PIPPIN]: 'wss://events.user-management-service.pippin.howfm.io/event/realtime',
  [WebAppEnvironment.BILBO]: 'wss://events.user-management-service.bilbo.howfm.io/event/realtime',
  [WebAppEnvironment.STAGING]: 'wss://events.user-management-service.develop.howfm.io/event/realtime',
  [WebAppEnvironment.PRODUCTION]: 'wss://events.user-management-service.production.howfm.io/event/realtime',
};

// ------------- Reconnection Configuration -------------

/**
 * Default reconnection configuration
 * - initialDelay: Start with 1 second delay
 * - maxDelay: Cap at 30 seconds
 * - maxAttempts: Try up to 10 times before giving up
 */
export const DEFAULT_RECONNECTION_CONFIG: ReconnectionConfig = {
  initialDelay: 1000,
  maxDelay: 30000,
  maxAttempts: 10,
};

// ------------- Connection Timeout -------------

/**
 * Connection timeout in milliseconds
 * If connection_ack is not received within this time, connection is considered failed
 */
export const CONNECTION_TIMEOUT_MS = 30000;

// ------------- WebSocket Close Codes -------------

/**
 * Standard WebSocket close codes with human-readable meanings
 * @see https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent/code
 */
export enum WsCloseCode {
  NORMAL_CLOSURE = 1000,
  GOING_AWAY = 1001,
  PROTOCOL_ERROR = 1002,
  UNSUPPORTED_DATA = 1003,
  NO_STATUS_RECEIVED = 1005,
  ABNORMAL_CLOSURE = 1006,
  INVALID_FRAME_PAYLOAD = 1007,
  POLICY_VIOLATION = 1008,
  MESSAGE_TOO_BIG = 1009,
  MANDATORY_EXTENSION = 1010,
  INTERNAL_SERVER_ERROR = 1011,
  SERVICE_RESTART = 1012,
  TRY_AGAIN_LATER = 1013,
  BAD_GATEWAY = 1014,
  TLS_HANDSHAKE_FAILED = 1015,
}

/**
 * Human-readable messages for WebSocket close codes
 */
export const CLOSE_CODE_MESSAGES: Record<WsCloseCode, string> & Record<number, string | undefined> = {
  [WsCloseCode.NORMAL_CLOSURE]: 'Connection closed normally',
  [WsCloseCode.GOING_AWAY]: 'Server is going away (e.g., server shutdown or browser navigating away)',
  [WsCloseCode.PROTOCOL_ERROR]: 'Protocol error',
  [WsCloseCode.UNSUPPORTED_DATA]: 'Unsupported data type',
  [WsCloseCode.NO_STATUS_RECEIVED]: 'No status code received',
  [WsCloseCode.ABNORMAL_CLOSURE]: 'Connection closed abnormally (no close frame received)',
  [WsCloseCode.INVALID_FRAME_PAYLOAD]: 'Invalid frame payload data',
  [WsCloseCode.POLICY_VIOLATION]: 'Policy violation',
  [WsCloseCode.MESSAGE_TOO_BIG]: 'Message too large',
  [WsCloseCode.MANDATORY_EXTENSION]: 'Missing required extension',
  [WsCloseCode.INTERNAL_SERVER_ERROR]: 'Internal server error',
  [WsCloseCode.SERVICE_RESTART]: 'Service restart',
  [WsCloseCode.TRY_AGAIN_LATER]: 'Try again later',
  [WsCloseCode.BAD_GATEWAY]: 'Bad gateway',
  [WsCloseCode.TLS_HANDSHAKE_FAILED]: 'TLS handshake failed',
};

/**
 * Get human-readable message for a WebSocket close code
 */
export function getCloseCodeMessage(code: number): string {
  return CLOSE_CODE_MESSAGES[code] ?? `Unknown close code: ${code}`;
}

// ------------- Activity Store Configuration -------------

/**
 * Maximum number of recent activities to keep in store
 */
export const MAX_RECENT_ACTIVITIES = 50;

// ------------- Channel Configuration -------------

/**
 * Get the activity channel name for a user
 */
export function getActivityChannel(userId: string): string {
  return `activities/${userId}`;
}
